import { useState, useEffect, useRef, type Key } from "react";
import AntDTable from "@/common/components/ui/antd-table";
import { Card, CardContent } from "@/common/components/ui/card";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import { Badge } from "@/common/components/ui/badge";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  History,
  RefreshCw,
  Download,
  FileText,
  Upload,
} from "lucide-react";
import { GoldService } from "@/services/APIs";
import HistoryLedgerDialog from "./components/HistoryLedgerDialog";
import { useNavigate } from "react-router-dom";
import finEaseHomeNavigationRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";
import DeleteConfirmationModal from "@/projects/FinEaseHomeAndAdmin/components/DeleteConfirmationModal";
import { ProductPayloadDataRes } from "@/utils/types/Gold";
import { notify } from "@/common/components/common/Notification";

const StockColumn = ({ productId }: { productId: string }) => {
  const [variants, setVariants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = await GoldService.getProductById(productId);
        if (response.success && response.data) {
          setVariants(response.data.variants || []);
        }
      } catch (error) {
        console.error("Failed to fetch product details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [productId]);

  if (loading) {
    return (
      <div className="flex justify-center">
        <RefreshCw className="h-3 w-3 animate-spin text-slate-300" />
      </div>
    );
  }
  if (!variants || variants.length === 0) {
    return <span className="text-muted-foreground">0</span>;
  }

  return (
    <div className="flex min-w-full flex-col gap-1.5 px-2 py-1">
      {variants.map((variant, idx) => (
        <div
          key={variant.id ?? idx}
          className="flex items-center justify-between gap-4 border-b border-slate-50/50 pb-1 last:border-0"
        >
          <span className="text-[12px] font-medium text-black">
            {variant.sku_code ?? variant.sku ?? "N/A"}
          </span>
          <span
            className={`text-[12px] font-bold ${
              Number(variant.current_stock) < 10
                ? "text-amber-600"
                : "text-secondary"
            }`}
          >
            {variant.current_stock}
          </span>
        </div>
      ))}
      {variants.length > 1 && (
        <div className="mt-1 flex items-center justify-between border-t border-slate-100 pt-1.5">
          <span className="text-[11px] font-bold uppercase tracking-tight text-slate-600">
            Total
          </span>
          <span className="text-[13px] font-extrabold text-primary">
            {variants.reduce((acc, v) => acc + Number(v.current_stock ?? 0), 0)}
          </span>
        </div>
      )}
    </div>
  );
};

const ProductMaster = () => {
  const [products, setProducts] = useState<ProductPayloadDataRes[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] =
    useState<ProductPayloadDataRes | null>(null);
  const navigate = useNavigate();
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [productToDelete, setProductToDelete] =
    useState<ProductPayloadDataRes | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleDeleteProduct = async () => {
    if (!productToDelete) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await GoldService.deleteProduct(productToDelete.id);
      if (response.success) {
        notify.success("Success", "Product deleted successfully");
        fetchProducts();
        setIsDeleteDialogOpen(false);
        setProductToDelete(null);
      } else {
        throw new Error(response.message ?? "Failed to delete product");
      }
    } catch (error: any) {
      notify.error("Error", error.message ?? "An unexpected error occurred");
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await GoldService.getProducts(currentPage, pageSize);
      const data: any = response.data;
      const productList = Array.isArray(data) ? data : (data?.results ?? []);
      setProducts(productList);
    } catch (_error) {
      notify.error("Error", "Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products?.filter(
    p =>
      (p.product_name ?? "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.sku_code ?? "").toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalCount = filteredProducts?.length ?? 0;

  const handleExport = async () => {
    if (selectedRowKeys.length === 0) {
      notify.error(
        "No products selected",
        "Use the checkboxes to select one or more products, then click Export."
      );
      return;
    }

    setIsExporting(true);
    try {
      const productIds = selectedRowKeys.map(key => String(key));
      const blob = await GoldService.exportExcel(productIds);

      if (blob && blob.size > 0) {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `product_master_${new Date().toISOString().split("T")[0]}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);

        notify.success(
          "Exported",
          "Excel file generated and download started."
        );
      } else {
        throw new Error("Failed to generate export file (empty response).");
      }
    } catch (error: any) {
      notify.error(
        "Export Failed",
        error.message ?? "An unexpected error occurred during export."
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleImport = () => {
    importInputRef.current?.click();
  };

  const handleImportFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const allowedExtensions = [".xlsx", ".xls"];
    const fileName = file.name.toLowerCase();
    const isExcel = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!isExcel) {
      notify.error(
        "Invalid File Format",
        "Only Excel files (.xlsx, .xls) are allowed. Please upload a valid Excel file."
      );
      e.target.value = "";
      return;
    }

    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await GoldService.importExcel(formData);
      if (response.success) {
        notify.success(
          "Import Success",
          response.message ?? "Product data has been imported successfully."
        );
        fetchProducts(); // Refresh list
      } else {
        throw new Error(response.message ?? "Failed to import excel file.");
      }
    } catch (error: any) {
      notify.error(
        "Import Failed",
        error.message ?? "An unexpected error occurred during import."
      );
    } finally {
      setIsImporting(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-6 p-6 pb-20">
      {/* Action Bar */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
        <svg
          className="absolute right-0 top-0 h-full w-1/3 text-white/5"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon fill="currentColor" points="50,0 100,0 100,100 0,100" />
        </svg>
        <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-white">
                Product Master
              </h1>
              <p className="text-white/70">
                Create, edit, and manage all Product Records
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={fetchProducts}
              disabled={loading}
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />{" "}
              Sync Data
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={handleExport}
              disabled={selectedRowKeys.length === 0 || isExporting}
            >
              {isExporting ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {isExporting ? "Exporting..." : "Export"}
              {!isExporting && selectedRowKeys.length > 0 && (
                <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
                  {selectedRowKeys.length}
                </span>
              )}
            </Button>
            {/* Hidden file input — Excel only */}
            <input
              ref={importInputRef}
              type="file"
              className="hidden"
              onChange={handleImportFileChange}
            />
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={handleImport}
              disabled={isImporting}
            >
              {isImporting ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {isImporting ? "Importing..." : "Import"}
            </Button>
          </div>
        </div>
      </div>
      <Card className="border-none shadow-sm">
        <CardContent className="flex flex-col justify-between gap-4 p-4 md:flex-row md:items-center">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by product name or SKU..."
              className="border-gray-100 bg-gray-50/50 pl-9 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3">
            <Button
              className="gap-2 shadow-lg shadow-primary/20"
              onClick={() =>
                navigate(finEaseHomeNavigationRoutes.PHYSICAL_GOLD_ADD_PRODUCT)
              }
            >
              <Plus className="h-4 w-4" /> Add New Product
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <AntDTable
        showSettings
        rowSelection={{
          selectedRowKeys,
          onChange: keys => setSelectedRowKeys(keys),
          preserveSelectedRowKeys: true,
          columnWidth: 48,
        }}
        columns={[
          {
            title: "Active",
            dataIndex: "is_enabled",
            key: "is_enabled",
            width: 50,
            align: "center",
            render: enabled => (
              <div className="flex justify-center">
                <div
                  className={`h-2.5 w-2.5 rounded-full ${enabled ? "bg-green-500" : "bg-gray-300"} shadow-sm`}
                />
              </div>
            ),
          },
          {
            title: "Product Details",
            key: "details",
            width: 100,
            render: (_, product) => (
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-md bg-gray-100 shadow-inner">
                  {product.main_image ? (
                    <img
                      src={product.main_image}
                      alt={product.product_name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gray-100 text-[10px] text-gray-400">
                      IMG
                    </div>
                  )}
                </div>
                <div className="flex flex-col overflow-hidden">
                  <span className="truncate font-bold text-secondary transition-colors group-hover:text-primary">
                    {product.product_name}
                  </span>
                </div>
              </div>
            ),
          },
          {
            title: "Metal/Purity",
            key: "metal_purity",
            width: 120,
            render: (_, product) => (
              <div className="flex flex-col">
                <span className="text-sm font-bold text-secondary">
                  {product.metal ?? "Gold"}
                </span>
                <span className="text-xs text-muted-foreground">
                  {product.purity ?? "24K"}
                </span>
              </div>
            ),
          },
          {
            title: "Shape",
            dataIndex: "shape",
            key: "shape",
            width: 100,
            align: "center",
            render: shape => (
              <Badge
                variant="secondary"
                className="bg-slate-100 text-[10px] font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-200"
              >
                {shape ?? "N/A"}
              </Badge>
            ),
          },
          {
            title: "Variants",
            key: "variants",
            width: 80,
            render: (_, product) => (
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-bold text-secondary">
                  {product.variant_count ?? 0} Variants
                </span>
                {product.variants && product.variants.length > 0 && (
                  <span className="text-[10px] text-muted-foreground">
                    {product.variants
                      .slice(0, 3)
                      .map(v => `${v.weight_grams}g`)
                      .join(", ")}
                    {product.variants.length > 3 && "..."}
                  </span>
                )}
              </div>
            ),
          },
          {
            title: "MRP (₹)",
            key: "mrp",
            width: 120,
            align: "right",
            render: (_, product) => (
              <span className="font-bold text-secondary">
                {Number(product.mrp).toLocaleString("en-IN")}
              </span>
            ),
          },
          {
            title: "Selling Price (₹)",
            key: "selling_price",
            width: 130,
            align: "center",
            render: (_, product) => (
              <span className="font-bold text-secondary">
                {Number(product.selling_price).toLocaleString("en-IN")}
              </span>
            ),
          },
          {
            title: "Stock Details",
            key: "stock",
            width: 200,
            align: "center",
            render: (_, product) => <StockColumn productId={product.id} />,
          },
          {
            title: "Origin",
            key: "country_of_origin",
            width: 120,
            render: (_, product) => (
              <span className="text-sm font-medium text-slate-700">
                {product.country_of_origin}
              </span>
            ),
          },
          {
            title: "Actions",
            key: "actions",
            align: "center",
            width: 140,
            render: (_, product) => (
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-amber-600 hover:bg-amber-50"
                  title="View History"
                  onClick={() => {
                    setSelectedProduct(product);
                    setIsHistoryOpen(true);
                  }}
                >
                  <History className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-primary hover:bg-primary/10"
                  title="Edit Product"
                  onClick={() =>
                    navigate(
                      finEaseHomeNavigationRoutes.PHYSICAL_GOLD_EDIT_PRODUCT(
                        product.id
                      )
                    )
                  }
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:bg-destructive/10"
                  title="Delete Product"
                  onClick={() => {
                    setProductToDelete(product);
                    setIsDeleteDialogOpen(true);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ),
          },
        ]}
        data={filteredProducts}
        loading={loading}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
      />

      <HistoryLedgerDialog
        open={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        selectedProduct={selectedProduct}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteProduct}
        title="Delete Product"
        message="Are you sure you want to delete"
        itemName={productToDelete?.product_name ?? "this product"}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ProductMaster;
